import { StyledPropsWithChildren } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import { getUuid } from '@front10/helpers/dist/uuid';
import React, { useContext, useEffect, useRef } from 'react';
import { getResizedImage } from './../../../../helpers/images';
import { useLazyLoading } from './../../../containers/_commons/lazyLoadImage';
import Box from './../../../../components/elements/Box';
import Text from './../../../../components/elements/Text';

type ImageProps = JSX.IntrinsicElements['img'];
type SelectedProps = Pick<ImageProps, 'alt'>;

interface ImageResizingParams {
  worker: string; // Base path of the image worker
  height: number;
  width: number;
}

interface AddedProps {
  /**
   * URI of the image.
   */
  image: ImageProps['src'];
  /**
   * Height of Destination Image
   */
  imageHeight: ImageProps['height'];
  /**
   * Width of Destination Image
   */
  imageWidth: ImageProps['width'];
  /**
   * Effect apply of the image
   */
  hoverEffect: 'grayscale' | 'fadeIn' | 'scale' | 'blur' | 'opacity' | 'sepia' | 'lightly' | 'dark';
  /**
   * Enable focusable in this components.
   */
  focusable: boolean;
  /**
   *  Children of the component
   */
  pathNoImageFound: string;
  noLabelImageSettings: $TSFixMe;
  focusClass: string;
  containerSettings: $TSFixMe;
  /**
   * Used to show or not the alternative text.
   */
  showNotFoundLabel: boolean;
  /**
   * Params for image resizing optimizations
   */
  imageResizingParams: ImageResizingParams;
  /**
   * Enable lazy loading
   */
  lazyLoadingEnable: boolean;
  /**
   * Forcing to load the images by https
   */
  forceHttpsProtocol: boolean;

  selectedImageEffect?: boolean;
  imageNotSelectedSettings?: any;
}

type Props = StyledPropsWithChildren<AddedProps & SelectedProps>;

const ImageHeader = (props: Props) => {
  const {
    imageHeight,
    imageWidth,
    hoverEffect,
    selectedImageEffect = false,
    alt,
    focusable,
    children,
    focusClass,
    containerSettings,
    showNotFoundLabel,
    pathNoImageFound,
    imageResizingParams,
    noLabelImageSettings,
    image = '',
    lazyLoadingEnable,
    forceHttpsProtocol,
    imageNotSelectedSettings
  } = props;

  const { direction, theme, translate } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const { observer } = useLazyLoading();
  let imageURL = image;
  if (forceHttpsProtocol) {
    imageURL = image?.replace('http://', 'https://');
  }

  const imageRef = useRef<HTMLDivElement>(null);
  const id = `fr-image-${getUuid()}`;

  useEffect(() => {
    const { current } = imageRef;
    if (observer && lazyLoadingEnable && current) {
      observer.observe(current);
    }
  });

  function noLabelImage() {
    return (
      <Box className="cmp-image-label" display="none">
        <Text {...noLabelImageSettings}>
          {translate('cmp-image-header-label-not-image-found', 'No Image Available')}
        </Text>
      </Box>
    );
  }

  const loadingImageStyle = {
    ...style,
    height: imageHeight,
    width: imageWidth,
    backgroundColor: 'lightgray',
    animation: 'pulse 1s infinite ease-in-out',
    backgroundImage: !lazyLoadingEnable
      ? `url(${getResizedImage(imageURL, imageResizingParams)})`
      : undefined,
    ...imageNotSelectedSettings
  };

  return (
    <Box
      id={id}
      tabIndex={focusable ? 0 : null}
      focusable={focusable}
      height="100"
      role={alt ? 'img' : undefined}
      dataTestId="imageHeader"
      ariaLabel={alt}
      focusClass={focusClass}
      className={`fr-${ImageHeader.componentName}-container`}
      {...containerSettings}
    >
      <Box
        className={`fr-image-header ${
          hoverEffect && `fr-effects fr-effect-${hoverEffect}`
        } ${className} ${selectedImageEffect ? `fr-image-selected-effects` : ''}`}
        style={loadingImageStyle}
        dataSrc={getResizedImage(imageURL, imageResizingParams)}
        dataDefaultImage={pathNoImageFound}
        focusReferences={imageRef}
      >
        {children}
        {showNotFoundLabel && noLabelImage()}
      </Box>
    </Box>
  );
};

ImageHeader.defaultProps = {
  image: '',
  imageHeight: '250px',
  imageWidth: '100%',
  selectedImageEffect: false,
  focusable: true,
  children: null,
  pathNoImageFound:
    'https://res.cloudinary.com/dakp804eh/image/upload/v1561045947/airmodules/Utils/NoImageFound.png',
  noLabelImageSettings: {
    position: 'absolute',
    padding: '3',
    bottom: '0',
    right: '0'
  },
  containerSettings: {},
  showNotFoundLabel: false,
  imageResizingParams: {
    worker: 'https://assets.airtrfx.com/cdn-cgi/image/',
    height: 500,
    width: 500,
    quality: 80,
    fit: 'crop',
    format: 'auto',
    opt: true
  },
  lazyLoadingEnable: true,
  forceHttpsProtocol: true
};

ImageHeader.componentName = 'ImageHeader';

export default ImageHeader;
